import {
  blockToMarkdown,
  blocksToMarkdown,
  type SerializableBlock,
} from '../blocks/serialize-text.js';

const PAGE_CONTAINER_TYPES = new Set(['page', 'database']);

export type SearchableBlock = {
  id: string;
  type: string;
  title?: string | null;
  content?: Record<string, unknown> | null;
  parentId?: string | null;
  position?: number;
  deletedAt?: unknown;
};

export function blockToSerializable(block: SearchableBlock, depth = 0): SerializableBlock {
  return {
    type: block.type,
    title: block.title,
    content: block.content,
    depth,
  };
}

export function indexBlocksByParent(
  blocks: SearchableBlock[],
): Map<string | null, SearchableBlock[]> {
  const byParent = new Map<string | null, SearchableBlock[]>();
  for (const block of blocks) {
    if (block.deletedAt != null && block.deletedAt !== '') {
      continue;
    }
    const parentId = block.parentId ?? null;
    const list = byParent.get(parentId);
    if (list) {
      list.push(block);
    } else {
      byParent.set(parentId, [block]);
    }
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }
  return byParent;
}

export function collectDescendantContentBlocks(
  rootIds: string[],
  childrenByParentId: Map<string | null, SearchableBlock[]>,
): SearchableBlock[] {
  const collected: SearchableBlock[] = [];
  let frontier = [...rootIds];

  while (frontier.length > 0) {
    const next: string[] = [];
    for (const parentId of frontier) {
      const children = childrenByParentId.get(parentId) ?? [];
      for (const child of children) {
        if (PAGE_CONTAINER_TYPES.has(child.type)) {
          next.push(child.id);
          continue;
        }
        collected.push(child);
        next.push(child.id);
      }
    }
    frontier = next;
  }

  return collected;
}

/** Plain title + body text indexed for one block (mirrors server FTS worker). */
export function buildBlockSearchText(
  block: SearchableBlock,
  childrenByParentId: Map<string | null, SearchableBlock[]>,
): { title: string; body: string } {
  const title = block.title?.trim() ?? '';

  if (PAGE_CONTAINER_TYPES.has(block.type)) {
    const children = collectDescendantContentBlocks([block.id], childrenByParentId);
    const body = blocksToMarkdown(
      children.map((child, index) => blockToSerializable(child, index)),
    );
    return { title, body };
  }

  const body = blockToMarkdown(blockToSerializable(block)).trim();
  return { title, body };
}
